import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Zap, Sun, Bird, Crown, Trophy, Lock, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { flame: Flame, zap: Zap, sun: Sun, bird: Bird, crown: Crown, trophy: Trophy };

const BadgeTile = ({ b, onTap, index }) => {
  const Icon = ICONS[b.icon] || Flame;
  const unlocked = b.unlocked;
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + index * 0.04 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onTap(b)}
      data-testid={`badge-tile-${b.id}`}
      className="relative flex flex-col items-center gap-2 p-3 rounded-2xl border border-zinc-800 bg-zinc-900/60 hover:border-zinc-700 transition-all"
    >
      <div
        className={`h-14 w-14 rounded-full grid place-items-center transition-all ${unlocked ? "shadow-lg" : ""}`}
        style={unlocked ? {
          background: `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})`,
          boxShadow: `0 8px 30px -8px ${b.gradient[1]}`,
        } : {
          background: "linear-gradient(135deg, #1f2937, #0f172a)",
        }}
      >
        {unlocked ? (
          <Icon className="h-6 w-6 text-white" strokeWidth={2.2} />
        ) : (
          <Lock className="h-5 w-5 text-zinc-600" strokeWidth={2.2} />
        )}
        {unlocked && (
          <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-lime-400 grid place-items-center border-2 border-zinc-900">
            <Check className="h-3 w-3 text-black" strokeWidth={3} />
          </div>
        )}
      </div>
      <div className={`text-[11px] font-semibold leading-tight text-center ${unlocked ? "text-white" : "text-zinc-500"}`}>
        {b.name}
      </div>
      <div className={`text-[10px] leading-tight ${unlocked ? "text-zinc-400" : "text-zinc-600"}`}>
        {unlocked ? `${b.threshold}d` : `${b.days_remaining}d to go`}
      </div>
    </motion.button>
  );
};

const BadgeDetail = ({ b, onClose }) => {
  if (!b) return null;
  const Icon = ICONS[b.icon] || Flame;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center px-6"
      data-testid="badge-detail-modal"
    >
      <motion.div
        initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-zinc-900 border border-zinc-800 rounded-3xl p-6 text-center text-white"
      >
        <div
          className="mx-auto h-24 w-24 rounded-full grid place-items-center"
          style={{
            background: b.unlocked
              ? `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})`
              : "linear-gradient(135deg, #1f2937, #0f172a)",
            boxShadow: b.unlocked ? `0 16px 50px -12px ${b.gradient[1]}` : "none",
          }}
        >
          {b.unlocked ? <Icon className="h-12 w-12 text-white" strokeWidth={2.2} /> : <Lock className="h-9 w-9 text-zinc-600" />}
        </div>
        <h3 className="mt-4 text-2xl font-bold">{b.name}</h3>
        <p className="mt-1 text-sm text-zinc-400">{b.tag}</p>
        <div className="mt-4 text-xs text-zinc-500">
          {b.unlocked ? `Earned at ${b.threshold} day streak` : `${b.days_remaining} more day${b.days_remaining === 1 ? "" : "s"} until unlock`}
        </div>
        <button
          onClick={onClose}
          className="mt-5 w-full rounded-full h-11 bg-zinc-800 text-zinc-200 font-medium hover:bg-zinc-700"
          data-testid="badge-detail-close"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

export default function BadgesGrid({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await api.streakBadges();
        if (!cancelled) setData(d);
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 flex items-center gap-2 text-sm text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading badges…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="badges-grid">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-white">Streak badges</h3>
        <div className="text-xs text-zinc-400">
          <span className="text-white font-semibold">{data.earned_count}</span> / {data.total_count}
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        {data.next_threshold
          ? `Next unlock at ${data.next_threshold} days — keep showing up`
          : "Every badge earned. Legend status."}
      </p>
      <div className="grid grid-cols-4 gap-2">
        {data.badges.map((b, i) => (
          <BadgeTile key={b.id} b={b} index={i} onTap={setOpen} />
        ))}
      </div>

      {open && <BadgeDetail b={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
